import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { observer } from 'mobx-react-lite';
import { userStore } from '../stores/UserStore';
import { batchStore } from '../stores/BatchStore';
import { academyStore } from '../stores/AcademyStore';
const UserDialog = observer(({ show, onHide, user, isEditing, role }) => {
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        roles: [],
        delegateAdmin: false,
        speciality: '',
        selectedBatchId: ''
    });
    const [errors, setErrors] = useState({});
    useEffect(() => {
        if (isEditing && user) {
            setFormData({
                name: user.name,
                phone: user.phone || '',
                email: user.email || '',
                roles: [...user.roles],
                delegateAdmin: user.roles.includes('admin'),
                speciality: user.speciality || '',
                selectedBatchId: ''
            });
        }
        else {
            // For new users, set the role based on the table they're adding from
            const roles = role ? [role] : [];
            setFormData({
                name: '',
                phone: '',
                email: '',
                roles: roles,
                delegateAdmin: false,
                speciality: '',
                selectedBatchId: ''
            });
        }
    }, [isEditing, user, role]);
    // Fetch batches when dialog opens for adding a player
    useEffect(() => {
        if (show && !isEditing && role === 'player' && academyStore.currentAcademy) {
            batchStore.fetchBatches(academyStore.currentAcademy.id);
        }
    }, [show, isEditing, role]);
    const handleInputChange = (e) => {
        const { name, value, type } = e.target;
        const checked = e.target.checked;
        if (name === 'delegateAdmin') {
            setFormData(prev => {
                const newRoles = checked
                    ? [...prev.roles.filter(r => r !== 'admin'), 'admin']
                    : prev.roles.filter(r => r !== 'admin');
                return {
                    ...prev,
                    [name]: checked,
                    roles: newRoles
                };
            });
        }
        else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
    };
    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }
        if (formData.phone && !/^[\+]?[1-9][\d]{0,15}$/.test(formData.phone)) {
            newErrors.phone = 'Please enter a valid phone number';
        }
        if (formData.roles.length === 0) {
            newErrors.roles = 'At least one role is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            return;
        }
        try {
            const userData = {
                name: formData.name.trim(),
                phone: formData.phone.trim() || undefined,
                email: formData.email.trim() || undefined,
                roles: formData.roles,
                speciality: formData.speciality.trim() || undefined
            };
            if (isEditing && user) {
                await userStore.updateUser(user.id, userData);
            }
            else {
                const createdUser = await userStore.createUser(userData);
                // If a batch is selected and user was created successfully, add player to batch
                if (createdUser && formData.selectedBatchId && role === 'player') {
                    try {
                        await batchStore.addPlayersToBatch(formData.selectedBatchId, [createdUser.id]);
                    }
                    catch (batchError) {
                        console.error('Error adding player to batch:', batchError);
                        // Don't fail the entire operation if batch assignment fails
                        // The user was created successfully, just show a warning
                        userStore.error = userStore.error
                            ? `${userStore.error}. Note: Player created but batch assignment failed.`
                            : 'Player created but batch assignment failed.';
                    }
                }
            }
            onHide();
        }
        catch (error) {
            console.error('Error saving user:', error);
        }
    };
    const handleClose = () => {
        setFormData({
            name: '',
            phone: '',
            email: '',
            roles: [],
            delegateAdmin: false,
            speciality: '',
            selectedBatchId: ''
        });
        setErrors({});
        onHide();
    };
    const getTitle = () => {
        if (isEditing) {
            return 'Edit User';
        }
        return `Add ${role ? role.charAt(0).toUpperCase() + role.slice(1) : 'User'}`;
    };
    return (_jsxs(Modal, { show: show, onHide: handleClose, size: "lg", centered: true, children: [_jsx(Modal.Header, { closeButton: true, children: _jsx(Modal.Title, { children: getTitle() }) }), _jsxs(Form, { onSubmit: handleSubmit, children: [_jsxs(Modal.Body, { children: [userStore.error && (_jsx(Alert, { variant: "danger", className: "mb-3", children: userStore.error })), _jsxs(Form.Group, { className: "mb-3", children: [_jsx(Form.Label, { children: "Name *" }), _jsx(Form.Control, { type: "text", name: "name", value: formData.name, onChange: handleInputChange, isInvalid: !!errors.name, placeholder: "Enter user name" }), _jsx(Form.Control.Feedback, { type: "invalid", children: errors.name })] }), _jsxs(Form.Group, { className: "mb-3", children: [_jsx(Form.Label, { children: "Phone" }), _jsx(Form.Control, { type: "tel", name: "phone", value: formData.phone, onChange: handleInputChange, isInvalid: !!errors.phone, placeholder: "Enter phone number" }), _jsx(Form.Control.Feedback, { type: "invalid", children: errors.phone })] }), _jsxs(Form.Group, { className: "mb-3", children: [_jsx(Form.Label, { children: "Email" }), _jsx(Form.Control, { type: "email", name: "email", value: formData.email, onChange: handleInputChange, isInvalid: !!errors.email, placeholder: "Enter email address" }), _jsx(Form.Control.Feedback, { type: "invalid", children: errors.email })] }), (role === 'player' || (!role && formData.roles.includes('player'))) && (_jsxs(Form.Group, { className: "mb-3", children: [_jsx(Form.Label, { children: "Speciality" }), _jsxs(Form.Select, { name: "speciality", value: formData.speciality, onChange: handleInputChange, children: [_jsx("option", { value: "", children: "Select speciality" }), _jsx("option", { value: "Batsman", children: "Batsman" }), _jsx("option", { value: "Bowler", children: "Bowler" }), _jsx("option", { value: "All-rounder", children: "All-rounder" }), _jsx("option", { value: "Wicket-keeper", children: "Wicket-keeper" })] }), _jsx(Form.Text, { className: "text-muted", children: "Player's specialization (optional)" })] })), !isEditing && role === 'player' && (_jsxs(Form.Group, { className: "mb-3", children: [_jsx(Form.Label, { children: "Assign to Batch" }), _jsxs(Form.Select, { name: "selectedBatchId", value: formData.selectedBatchId, onChange: handleInputChange, children: [_jsx("option", { value: "", children: "No batch (assign later)" }), batchStore.batches.map((batch) => (_jsx("option", { value: batch.id, children: batch.name }, batch.id)))] }), _jsx(Form.Text, { className: "text-muted", children: "Optionally assign this player to a batch immediately" })] })), role === 'coach' && (_jsxs(Form.Group, { className: "mb-3", children: [_jsx(Form.Check, { type: "checkbox", name: "delegateAdmin", label: "Delegate Admin", checked: formData.delegateAdmin, onChange: handleInputChange }), _jsx(Form.Text, { className: "text-muted", children: "Check this if the coach should also have admin privileges" })] })), !role && (_jsxs(Form.Group, { className: "mb-3", children: [_jsx(Form.Label, { children: "Roles *" }), _jsx("div", { children: ['admin', 'coach', 'player'].map(roleOption => (_jsx(Form.Check, { type: "checkbox", id: `role-${roleOption}`, label: roleOption.charAt(0).toUpperCase() + roleOption.slice(1), checked: formData.roles.includes(roleOption), onChange: (e) => {
                                                const newRoles = e.target.checked
                                                    ? [...formData.roles, roleOption]
                                                    : formData.roles.filter(r => r !== roleOption);
                                                setFormData(prev => ({ ...prev, roles: newRoles }));
                                            } }, roleOption))) }), errors.roles && (_jsx("div", { className: "text-danger small", children: errors.roles }))] }))] }), _jsxs(Modal.Footer, { children: [_jsx(Button, { variant: "secondary", onClick: handleClose, className: "btn-touch w-mobile-100", children: "Cancel" }), _jsx(Button, { variant: "primary", type: "submit", disabled: userStore.loading, className: "btn-touch w-mobile-100", children: userStore.loading ? 'Saving...' : (isEditing ? 'Update' : 'Create') })] })] })] }));
});
export default UserDialog;
